import React from 'react';
import classnames from 'classnames';
import { Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogActions } from '@mui/material';

import { MoreHorizRounded, MoreVert as MoreVertIcon } from '@mui/icons-material';
import { Button } from 'new/elements/Button';
import Tooltip from '../mui/Tooltip';
import { has } from 'lodash';

import './Menu.scss';
import { IconButton, Popover } from '@mui/material';
import Typography from 'new/elements/TypographySupport';
import { Stack } from 'new/elements/Stack';

class Menu extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      currentSubMenuItem: null,
      isPopUpOpen: false,
      openDialogItemLabel: null,
    };

    this._openMenuButton = React.createRef();

    this._renderPopUpContent = this._renderPopUpContent.bind(this);
    this._getListItem = this._getListItem.bind(this);
    this._closePopUp = this._closePopUp.bind(this);
    this._handleOpenPopUpClicked = this._handleOpenPopUpClicked.bind(this);
    this._handleItemClicked = this._handleItemClicked.bind(this);
    this._handleBackClicked = this._handleBackClicked.bind(this);
    this._handleDialogOpen = this._handleDialogOpen.bind(this);
    this._handleDialogClose = this._handleDialogClose.bind(this);
  }

  render() {
    const { inPipeline, horizontalDots, menuButtonClassName } = this.props;
    const { isPopUpOpen } = this.state;
    const buttonClassName = classnames(menuButtonClassName, 'menu-button', {
      'menu-pop-up-open': isPopUpOpen,
    });

    return (
      <div className={classnames({ 'menu-in-pipeline': inPipeline })}>
        <IconButton
          arial-label="more-menu"
          ref={this._openMenuButton}
          data-testid="open-menu-button"
          className={buttonClassName}
          onClick={this._handleOpenPopUpClicked}
          style={{ display: 'flex', alignItems: 'center' }}
        >
          {horizontalDots ? (
            <MoreHorizRounded style={{ fontSize: 20 }} />
          ) : (
            <MoreVertIcon style={{ fontSize: 20 }} />
          )}
        </IconButton>
        <Popover
          className={this.props.menuClassName}
          open={isPopUpOpen}
          elevation={7}
          anchorEl={this._openMenuButton?.current}
          onClose={this._closePopUp}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          {this._renderPopUpContent()}
        </Popover>
      </div>
    );
  }

  _renderPopUpContent() {
    const { items, inPipeline } = this.props;
    const { currentSubMenuItem } = this.state;
    const displayedItems = currentSubMenuItem ? currentSubMenuItem.children : items;

    return (
      <ul
        className={classnames('menu-pop-up-content', {
          'menu-content-in-pipeline': inPipeline,
        })}
      >
        {currentSubMenuItem && this._getListItem(currentSubMenuItem, -1)}
        {displayedItems.map(this._getListItem)}
      </ul>
    );
  }

  _getListItem(item, index) {
    if (item.type === 'heading') {
      return (
        <li data-testid={`menu-option-${item.label}`} key={index} className="menu-li-heading">
          {!!item.label && <Typography variant="overline">{item.label}</Typography>}
        </li>
      );
    }

    const isDisabled = has(item, 'disable') && item.disable === true;
    const isBackItem = this.state.currentSubMenuItem === item;
    const requiresConfirmation = item.type === 'dialog';

    let onClick = () => {};
    let wrapInPopup = false,
      popupMessage = null;
    if (isDisabled) {
      wrapInPopup = true;
      popupMessage = has(item, 'disableMessage')
        ? item.disableMessage
        : 'This action is not allowed';
    } else {
      if (isBackItem) {
        onClick = this._handleBackClicked;
      } else if (requiresConfirmation) {
        onClick = (event) => {
          event.stopPropagation();
          this._handleDialogOpen(item.label);
        };
      } else {
        onClick = (event) => this._handleItemClicked(event, item);
      }
    }

    let dialogCta = null;
    if (requiresConfirmation) {
      let confirmLabel = has(item, 'confirmLabel') ? item.confirmLabel : 'Yes';
      let discardLabel = has(item, 'discardLabel') ? item.discardLabel : 'No';
      let primaryButtonColor = item?.primaryButtonColor || 'primary';
      let dialogMessage = has(item, 'dialogMessage') ? item.dialogMessage : 'Are you sure?';

      dialogCta = (
        <Dialog
          key="dialog-cta-container-key"
          open={this.state.openDialogItemLabel === item.label}
          fullWidth
          maxWidth="sm"
          onClose={(event) => {
            event.preventDefault();
            event.stopPropagation();
            this._handleDialogClose(event, item, false);
          }}
        >
          <DialogContent>{dialogMessage}</DialogContent>
          <DialogActions>
            <Button
              data-testid="menu-popup-discard-button"
              variant="contained"
              color="info"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                this._handleDialogClose(null, item, false);
              }}
            >
              {discardLabel}
            </Button>
            <Button
              data-testid="menu-popup-confirm-button"
              color={primaryButtonColor}
              variant="contained"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                this._handleDialogClose(null, item, true);
              }}
            >
              {confirmLabel}
            </Button>
          </DialogActions>
        </Dialog>
      );
    }

    const menuItemContent = [
      <Stack
        key="menu-item-container"
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography
          key="menu-item-container"
          color={item.color}
          className={classnames('menu-item-container', {
            'menu-item-back-button': isBackItem,
          })}
        >
          {!!(item.icon || item.iconPath) && (
            <span className="menu-item-icon-holder">
              {item.icon || <img src={item.iconPath} alt="" />}
            </span>
          )}
          {item.label}
        </Typography>
        {item.endView}
      </Stack>,
      requiresConfirmation && dialogCta,
      item.children && (
        <i
          key="menu-children-indicator"
          className={classnames('fa menu-children-indicator', {
            'fa-angle-right': !isBackItem,
            'fa-angle-left': isBackItem,
          })}
          aria-hidden="true"
        />
      ),
    ];

    const thisMenuItem = (
      <li
        className={classnames({ 'menu-item-disabled': isDisabled })}
        key={index}
        data-testid={`menu-item-${item.label}`}
        onClick={onClick}
      >
        {(!isDisabled && item.href && (
          <Link className="undecorated-link" to={item.href}>
            {menuItemContent}
          </Link>
        )) ||
          menuItemContent}
      </li>
    );

    return wrapInPopup && popupMessage ? (
      <Tooltip key={index} title={popupMessage} arrow={true} className="item-disabled">
        {thisMenuItem}
      </Tooltip>
    ) : (
      thisMenuItem
    );
  }

  _closePopUp(e) {
    if (this.props.onClose) {
      this.props.onClose(e);
    }
    if (e) {
      e.stopPropagation();
    }
    this.setState({
      currentSubMenuItem: null,
      isPopUpOpen: false,
    });
  }

  _handleOpenPopUpClicked(event) {
    event.stopPropagation();
    if (this.props.onOpen) {
      this.props.onOpen(event);
    }

    this.setState({
      currentSubMenuItem: null,
      isPopUpOpen: true,
    });
  }

  _handleItemClicked(event, item) {
    if (event) {
      event.stopPropagation();
    }
    if (item.children) {
      this.setState({ currentSubMenuItem: item });
    } else {
      if (item.onClick) {
        item.onClick(item, event);
      }
      if (!item.preventCloseOnClick) {
        this._closePopUp();
      }
    }
  }

  _handleBackClicked(event) {
    event.stopPropagation();
    this.setState({ currentSubMenuItem: null });
  }

  _handleDialogOpen(label) {
    this.setState({ openDialogItemLabel: label });
  }

  _handleDialogClose(event = null, item, confirmed = false) {
    this.setState({ openDialogItemLabel: null }, () => {
      if (confirmed) {
        this._handleItemClicked(event || null, item);
      }
    });
  }
}

export default Menu;
